/**
 * founsiHistorySlice — list of past Founsi threads.
 *
 * Owns the thread index that the HistorySheet renders.  Resume +
 * replay logic lives on `founsiSlice` (the in-flight chat slice) —
 * this slice is intentionally read-mostly so the sheet doesn't
 * couple to chat state.
 *
 * Scope (PR1 of #261):
 *   - `loadThreads()` (initial fetch, replaces the list)
 *   - `refreshThreads()` (re-pull head, used after sending a new
 *     message so a freshly-created thread appears in the drawer)
 *   - `prependThread()` — optimistic insert when the SSE `thread`
 *     event lands for a brand-new thread, so the user can see their
 *     just-started conversation without a refresh
 *
 * Follow-ups (PR2/PR3 of #261):
 *   - renameThread / deleteThread (PR2)
 *   - searchThreads + infinite-scroll loadMoreThreads (PR3)
 */

import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import {
  deleteThread as deleteThreadApi,
  listThreads,
  patchThread,
  type ThreadSummary,
  ThreadsHttpError,
} from './api/threadsApi';

export interface FounsiHistoryState {
  threads: ThreadSummary[];
  loading: boolean;
  /** Human-readable error for the UI; null when no error. */
  error: string | null;
  /** Distinguishes "never loaded" from "loaded empty" so the empty
   *  state doesn't flash before the first fetch completes. */
  hasLoadedOnce: boolean;
  /** Whether more pages exist on the server — used by PR3 for
   *  infinite scroll. */
  hasMore: boolean;
  /** Pre-mutation snapshots keyed by thread id, used to roll back
   *  optimistic updates if the BE call fails.  Matches the
   *  FE-255 inflight-snapshot pattern used by every other slice. */
  inflight: Record<string, ThreadSummary | { deletedAt: number; row: ThreadSummary; index: number }>;
  /** Current search query.  Empty string = no filter (full list). */
  searchQuery: string;
  /** Current offset for infinite-scroll pagination. */
  offset: number;
  /** True while a loadMore page is in-flight; prevents double-fires
   *  from `onEndReached` firing repeatedly during fast scrolling. */
  loadingMore: boolean;
}

const initialState: FounsiHistoryState = {
  threads: [],
  loading: false,
  error: null,
  hasLoadedOnce: false,
  hasMore: false,
  inflight: {},
  searchQuery: '',
  offset: 0,
  loadingMore: false,
};

const DEFAULT_LIMIT = 20;

/** Initial load — replaces the list and resets pagination state.
 *  Honors the current `searchQuery` so re-opening the drawer with
 *  an active filter doesn't clobber it. */
export const loadThreads = createAsyncThunk<
  { threads: ThreadSummary[]; nextOffset: number | null; q: string },
  void,
  { state: { founsiHistory: FounsiHistoryState }; rejectValue: string }
>('founsiHistory/loadThreads', async (_, { getState, rejectWithValue, signal }) => {
  const q = getState().founsiHistory.searchQuery;
  try {
    const res = await listThreads({ limit: DEFAULT_LIMIT, q, signal });
    return {
      threads: res.threads,
      nextOffset: res.next_offset,
      q,
    };
  } catch (err) {
    const msg =
      err instanceof ThreadsHttpError
        ? err.message
        : (err as Error)?.message ?? 'Failed to load threads';
    return rejectWithValue(msg);
  }
});

/** Append the next page of threads for infinite scroll.  Skips if
 *  no more pages OR another loadMore is already in-flight. */
export const loadMoreThreads = createAsyncThunk<
  { threads: ThreadSummary[]; nextOffset: number | null },
  void,
  { state: { founsiHistory: FounsiHistoryState }; rejectValue: string }
>(
  'founsiHistory/loadMoreThreads',
  async (_, { getState, rejectWithValue, signal }) => {
    const s = getState().founsiHistory;
    const offset = s.threads.length; // BE offsets by row count, not page #
    try {
      const res = await listThreads({
        limit: DEFAULT_LIMIT,
        offset,
        q: s.searchQuery,
        signal,
      });
      return { threads: res.threads, nextOffset: res.next_offset };
    } catch (err) {
      const msg =
        err instanceof ThreadsHttpError
          ? err.message
          : (err as Error)?.message ?? 'Failed to load more';
      return rejectWithValue(msg);
    }
  },
  {
    // Don't fire if a loadMore is already in flight or we know we've
    // reached the end.
    condition: (_, { getState }) => {
      const s = getState().founsiHistory;
      return s.hasMore && !s.loadingMore && !s.loading;
    },
  },
);

/** Rename a thread.  Optimistic: applies the new title immediately,
 *  rolls back the row's prior title if the BE call fails. */
export const renameThread = createAsyncThunk<
  { id: string; title: string },
  { id: string; title: string },
  { state: { founsiHistory: FounsiHistoryState }; rejectValue: string }
>(
  'founsiHistory/renameThread',
  async ({ id, title }, { rejectWithValue, signal }) => {
    try {
      const res = await patchThread(id, { title }, { signal });
      return { id: res.id, title: res.title };
    } catch (err) {
      const msg =
        err instanceof ThreadsHttpError
          ? err.message
          : (err as Error)?.message ?? 'Failed to rename thread';
      return rejectWithValue(msg);
    }
  },
);

/** Pin / unpin a thread.  Optimistic: flips `pinned` immediately,
 *  rolls back on failure.  The HistorySheet's bucketing puts pinned
 *  threads in a dedicated bucket above Today. */
export const setThreadPinned = createAsyncThunk<
  { id: string; pinned: boolean },
  { id: string; pinned: boolean },
  { state: { founsiHistory: FounsiHistoryState }; rejectValue: string }
>(
  'founsiHistory/setThreadPinned',
  async ({ id, pinned }, { rejectWithValue, signal }) => {
    try {
      const res = await patchThread(id, { pinned }, { signal });
      return { id: res.id, pinned: res.pinned };
    } catch (err) {
      const msg =
        err instanceof ThreadsHttpError
          ? err.message
          : (err as Error)?.message ?? 'Failed to update thread';
      return rejectWithValue(msg);
    }
  },
);

/** Soft-delete a thread.  Optimistic: removes the row immediately
 *  (so the user sees a clean drawer), restores it on failure.  We
 *  pass the deleted row through `meta.arg.snapshot` so the rejected
 *  case can splice it back in at its original index. */
export const deleteThread = createAsyncThunk<
  { id: string },
  { id: string },
  { state: { founsiHistory: FounsiHistoryState }; rejectValue: string }
>(
  'founsiHistory/deleteThread',
  async ({ id }, { rejectWithValue, signal }) => {
    try {
      await deleteThreadApi(id, { signal });
      return { id };
    } catch (err) {
      const msg =
        err instanceof ThreadsHttpError
          ? err.message
          : (err as Error)?.message ?? 'Failed to delete thread';
      return rejectWithValue(msg);
    }
  },
);

/** Re-pull the head; used after sending a new message so a freshly
 *  created thread surfaces in the drawer without a manual reload.
 *  Semantically identical to `loadThreads` for PR1; kept separate so
 *  the action name reads correctly in the redux devtools timeline. */
export const refreshThreads = createAsyncThunk<
  { threads: ThreadSummary[]; hasMore: boolean },
  void,
  { state: { founsiHistory: FounsiHistoryState }; rejectValue: string }
>('founsiHistory/refreshThreads', async (_, { getState, rejectWithValue, signal }) => {
  const q = getState().founsiHistory.searchQuery;
  try {
    const res = await listThreads({ limit: DEFAULT_LIMIT, q, signal });
    return {
      threads: res.threads,
      hasMore: typeof res.next_offset === 'number',
    };
  } catch (err) {
    const msg =
      err instanceof ThreadsHttpError
        ? err.message
        : (err as Error)?.message ?? 'Failed to refresh threads';
    return rejectWithValue(msg);
  }
});

const slice = createSlice({
  name: 'founsiHistory',
  initialState,
  reducers: {
    /** Update the in-flight search query.  The actual fetch is
     *  triggered by the consumer via debounce — the slice just
     *  stores the canonical value so other places (analytics,
     *  empty-state copy) can read it. */
    setSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload;
    },
    /** Optimistic insert called when the SSE `thread` event lands
     *  for a brand-new thread — keeps the drawer feeling alive
     *  without a server round-trip. */
    prependThread(state, action: PayloadAction<ThreadSummary>) {
      // De-dup: if the thread is already at the head, replace it
      // (the BE-side title may have changed between SSE emission
      // and a later list call); otherwise prepend.
      const idx = state.threads.findIndex((t) => t.id === action.payload.id);
      if (idx >= 0) {
        state.threads[idx] = action.payload;
        // Move to head if not already there.
        if (idx > 0) {
          const [t] = state.threads.splice(idx, 1);
          state.threads.unshift(t);
        }
      } else {
        state.threads.unshift(action.payload);
      }
    },
  },
  extraReducers: (b) => {
    b.addCase(loadThreads.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    b.addCase(loadThreads.fulfilled, (state, action) => {
      // Drop the result if the search query changed between dispatch
      // and resolve — prevents stale-page flashes when the user is
      // typing quickly (the latest debounce will hit again).
      if (action.payload.q !== state.searchQuery) {
        state.loading = false;
        return;
      }
      state.threads = action.payload.threads;
      state.hasMore = typeof action.payload.nextOffset === 'number';
      state.offset = action.payload.threads.length;
      state.loading = false;
      state.hasLoadedOnce = true;
    });
    b.addCase(loadThreads.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload ?? 'Failed to load threads';
      state.hasLoadedOnce = true;
    });
    b.addCase(refreshThreads.fulfilled, (state, action) => {
      state.threads = action.payload.threads;
      state.hasMore = action.payload.hasMore;
      state.offset = action.payload.threads.length;
      // Don't toggle `loading` for refresh — the UI surfaces this
      // as a quiet re-fetch, not a full spinner.
    });

    // ── loadMoreThreads ──────────────────────────────────────────
    b.addCase(loadMoreThreads.pending, (state) => {
      state.loadingMore = true;
    });
    b.addCase(loadMoreThreads.fulfilled, (state, action) => {
      // Append the new page; dedupe by id in case the BE ever
      // returns an overlapping cursor (defensive — shouldn't happen
      // with offset/limit, but cheap insurance).
      const seen = new Set(state.threads.map((t) => t.id));
      for (const t of action.payload.threads) {
        if (!seen.has(t.id)) {
          state.threads.push(t);
          seen.add(t.id);
        }
      }
      state.hasMore = typeof action.payload.nextOffset === 'number';
      state.offset = state.threads.length;
      state.loadingMore = false;
    });
    b.addCase(loadMoreThreads.rejected, (state, action) => {
      state.loadingMore = false;
      state.error = action.payload ?? null;
    });
    b.addCase(refreshThreads.rejected, (state, action) => {
      // Silent failure for refresh — keep the existing list, surface
      // the error inline if the user opens the drawer.
      state.error = action.payload ?? null;
    });

    // ── renameThread ─────────────────────────────────────────────
    b.addCase(renameThread.pending, (state, action) => {
      const { id, title } = action.meta.arg;
      const row = state.threads.find((t) => t.id === id);
      if (!row) return;
      state.inflight[id] = { ...row }; // snapshot for rollback
      row.title = title;
    });
    b.addCase(renameThread.fulfilled, (state, action) => {
      delete state.inflight[action.payload.id];
      // The BE-confirmed title may be normalised (e.g. trimmed) —
      // apply it so the row matches the server.
      const row = state.threads.find((t) => t.id === action.payload.id);
      if (row) row.title = action.payload.title;
    });
    b.addCase(renameThread.rejected, (state, action) => {
      const id = action.meta.arg.id;
      const snap = state.inflight[id];
      if (snap && !('deletedAt' in snap)) {
        const row = state.threads.find((t) => t.id === id);
        if (row) row.title = snap.title;
      }
      delete state.inflight[id];
      state.error = action.payload ?? 'Rename failed';
    });

    // ── setThreadPinned ──────────────────────────────────────────
    b.addCase(setThreadPinned.pending, (state, action) => {
      const { id, pinned } = action.meta.arg;
      const row = state.threads.find((t) => t.id === id);
      if (!row) return;
      state.inflight[id] = { ...row };
      row.pinned = pinned;
    });
    b.addCase(setThreadPinned.fulfilled, (state, action) => {
      delete state.inflight[action.payload.id];
      const row = state.threads.find((t) => t.id === action.payload.id);
      if (row) row.pinned = action.payload.pinned;
    });
    b.addCase(setThreadPinned.rejected, (state, action) => {
      const id = action.meta.arg.id;
      const snap = state.inflight[id];
      if (snap && !('deletedAt' in snap)) {
        const row = state.threads.find((t) => t.id === id);
        if (row) row.pinned = snap.pinned;
      }
      delete state.inflight[id];
      state.error = action.payload ?? 'Pin failed';
    });

    // ── deleteThread ─────────────────────────────────────────────
    b.addCase(deleteThread.pending, (state, action) => {
      const id = action.meta.arg.id;
      const idx = state.threads.findIndex((t) => t.id === id);
      if (idx < 0) return;
      state.inflight[id] = {
        deletedAt: idx, // sentinel; full row in `row`
        row: { ...state.threads[idx] },
        index: idx,
      } as any;
      state.threads.splice(idx, 1);
    });
    b.addCase(deleteThread.fulfilled, (state, action) => {
      delete state.inflight[action.payload.id];
    });
    b.addCase(deleteThread.rejected, (state, action) => {
      const id = action.meta.arg.id;
      const snap = state.inflight[id];
      if (snap && 'deletedAt' in snap) {
        // Splice the row back in at its original index (or the
        // current end if the list has shrunk past it).
        const idx = Math.min(snap.index, state.threads.length);
        state.threads.splice(idx, 0, snap.row);
      }
      delete state.inflight[id];
      state.error = action.payload ?? 'Delete failed';
    });
  },
});

export const { prependThread, setSearchQuery } = slice.actions;
export default slice.reducer;
